import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useGetCategoriesQuery, useAddFeatureGroupMutation, useUpdateFeatureGroupMutation } from "../service/features/api";
import { toast } from "react-toastify";
import { VEHICLE_TYPE, Group_Type, Field_Type } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { getFieldTypeLabel } from "../utils/fieldTypes";

interface AddGroupProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  featureGroupData?: any;
}

const AddFeatureGroup: React.FC<AddGroupProps> = ({ open, setOpen, featureGroupData }) => {
  const [name, setName] = useState<string>("");
  const [groupType, setGroupType] = useState<string>("default");
  const [fieldType, setFieldType] = useState<string>("default");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");

  const [errors, setErrors] = useState<any>({});

  const { data: categoriesData } = useGetCategoriesQuery(
    { search: "", page: 1, limit: 100, type: selectedVehicleType },
    { skip: !selectedVehicleType }
  );
  const [addFeatureGroup, { isLoading: isAdding }] = useAddFeatureGroupMutation();
  const [updateFeatureGroup, { isLoading: isUpdating }] = useUpdateFeatureGroupMutation();

  useEffect(() => {
    if (featureGroupData && open) {
      setName(featureGroupData.name || "");
      setGroupType(featureGroupData.groupType?.toString() || "default");
      setFieldType(featureGroupData.fieldType?.toString() || "default");

      let catIds: string[] = [];
      if (featureGroupData.categories && Array.isArray(featureGroupData.categories)) {
        catIds = featureGroupData.categories.map((c: any) => c._id);
      } else if (featureGroupData.categoryId) {
        catIds = Array.isArray(featureGroupData.categoryId) ? featureGroupData.categoryId : [featureGroupData.categoryId];
      }
      setSelectedCategories(catIds);
      const vType = featureGroupData.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else if (!featureGroupData && open) {
      setName("");
      setGroupType("default");
      setFieldType("default");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [featureGroupData, open]);


  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    const categories = categoriesData?.data?.data || [];

    if (value.includes("all")) {
      if (selectedCategories.length === categories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(categories.map((cat: any) => cat._id));
      }
    } else {
      setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    }
    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    setSelectedCategories([]); // Reset categories when vehicle type changes
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const handleNameChange = (e: any) => {
    if (e.target.value.length <= 40) {
      setName(e.target.value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const validate = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Group Name is required / اسم المجموعة مطلوب";
      isValid = false;
    }
    if (groupType === "default") {
      tempErrors.groupType = "Group Type is required / نوع المجموعة مطلوب";
      isValid = false;
    }
    if (fieldType === "default") {
      tempErrors.fieldType = "Field Type is required / نوع الحقل مطلوب";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required / مطلوب فئة واحدة على الأقل";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      name,
      groupType: Number(groupType),
      fieldType: Number(fieldType),
      categoryId: selectedCategories,
      type: Number(selectedVehicleType),
    };

    try {
      if (featureGroupData?._id) {
        await updateFeatureGroup({ id: featureGroupData._id, ...payload }).unwrap();
        toast.success("Feature Group updated successfully / تم تحديث مجموعة الميزات بنجاح");
      } else {
        await addFeatureGroup(payload).unwrap();
        toast.success("Feature Group added successfully / تمت إضافة مجموعة الميزات بنجاح");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Operation failed / فشلت العملية");
    }
  };

  return (
    <Modal className="modal rejectReason_modal" open={open}>
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{featureGroupData ? "Edit Feature / تعديل الميزة" : "Add Feature / إضافة ميزة"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>


          </div>

          <div className="form ">
            <div className="gap_p">
              <div className="control_group w_100">
                <label>Name / الاسم</label>
                <TextField
                  fullWidth
                  hiddenLabel
                  placeholder="Enter Name / أدخل الاسم"
                  value={name}
                  onChange={handleNameChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  inputProps={{ maxLength: 40 }}
                />
              </div>

              <div className="control_group w_100">
                <label htmlFor="groupType">Feature Group Type / نوع مجموعة الميزات</label>
                <Select
                  fullWidth
                  hiddenLabel
                  value={groupType}
                  onChange={(e) => {
                    setGroupType(e.target.value);
                    if (errors.groupType) setErrors({ ...errors, groupType: "" });
                  }}
                  error={!!errors.groupType}
                >
                  <MenuItem value="default" disabled>Select Feature Group Type / اختر نوع مجموعة الميزات</MenuItem>
                  {Object.entries(Group_Type).map(([key, value]) => (
                    <MenuItem key={value} value={value.toString()}>
                      {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                    </MenuItem>
                  ))}
                </Select>
                {errors.groupType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.groupType}</div>}
              </div>

              <div className="control_group">
                <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
                <Select
                  fullWidth
                  hiddenLabel
                  displayEmpty
                  value={selectedVehicleType}
                  onChange={handleVehicleTypeChange}
                  renderValue={(selected) => {
                    if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                    return getVehicleTypeLabel(selected);
                  }}
                  error={!!errors.vehicleType}
                >
                  <MenuItem value="" disabled>Select Vehicle Type / اختر نوع المركبة</MenuItem>
                  {Object.keys(VEHICLE_TYPE)
                    .filter(k => isNaN(Number(k)))
                    .map((key) => (
                      <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                        {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                      </MenuItem>
                    ))}
                </Select>
                {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
              </div>

              <div className="control_group">
                <label>Category / الفئة</label>
                <Select
                  multiple
                  fullWidth
                  hiddenLabel
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  renderValue={(selected) => {
                    const selectedIds = (selected as string[]) || [];
                    if (selectedIds.length === 0) return selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً";

                    return selectedIds.map(id => {
                      const cat = categoriesData?.data?.data?.find((c: any) => c._id === id);
                      if (cat?.name) return cat.name;

                      // Fallback for edit mode if category not in current page
                      const source = featureGroupData?.categories || featureGroupData?.categoryId;
                      const items = Array.isArray(source) ? source : [source];
                      const found = items.find((c: any) => c && typeof c === 'object' && (c._id === id || c.id === id));
                      if (found?.name) return found.name;

                      return id;
                    }).join(", ");
                  }}
                  disabled={!selectedVehicleType}
                  error={!!errors.category}
                >
                  <MenuItem value="" disabled>
                    {selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً"}
                  </MenuItem>
                  {categoriesData?.data?.data?.length > 0 && (
                    <MenuItem value="all">
                      <em>{selectedCategories.length === categoriesData.data.data.length ? "Deselect All / إلغاء تحديد الكل" : "Select All / تحديد الكل"}</em>
                    </MenuItem>
                  )}
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      <Checkbox checked={selectedCategories.includes(cat._id)} />
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
              </div>

              <div className="control_group w_100">
                <label htmlFor="fieldType">Field Type / نوع الحقل</label>
                <Select
                  fullWidth
                  hiddenLabel
                  value={fieldType}
                  onChange={(e) => {
                    setFieldType(e.target.value);
                    if (errors.fieldType) setErrors({ ...errors, fieldType: "" });
                  }}
                  error={!!errors.fieldType}
                >
                  <MenuItem value="default" disabled>Select Field Type / اختر نوع الحقل</MenuItem>
                  {Object.entries(Field_Type)
                    .filter(([key]) => isNaN(Number(key)))
                    .map(([key, value]) => (
                      <MenuItem key={value} value={value.toString()}>{getFieldTypeLabel(value)}</MenuItem>
                    ))}
                </Select>
                {errors.fieldType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.fieldType}</div>}
              </div>
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_primary"
                onClick={handleSave}
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddFeatureGroup;